import request from "../utils/request";
import { CONNECT_CLIENT, CONNECT } from "../utils/definitions";
import { differenceInDays, fromYYYYMMDD, toYYYYMMDD } from "../utils/datefns";
import { getSession, setSession } from "../utils/localStorage";
import { gtmFunction } from "../utils/gtm";
import { generateSession } from "./session";

const getAvailability = ({ form, supplierId, unitId, type, packageId }) => {
  const { arrivalDate, departureDate, guests, childages} = processForm(form);
  let url = `${CONNECT}/lodging?id=${CONNECT_CLIENT}&arrivalDate=${arrivalDate}&departureDate=${departureDate}&guests=${guests}&childrenAges=${childages}`
  if (type === "activities" || type === "activity" || type === "restaurants" || type === "restaurant") url = `${CONNECT}/activities?id=${CONNECT_CLIENT}&arrivalDate=${arrivalDate}`
  if (supplierId) url += `&supplier=${supplierId}`;
  if (unitId) url += `&unit=${unitId}`;
  if (packageId) url += `&packageId=${packageId}`;

  return new Promise((resolve, reject) => {
    request({
      method: "GET",
      url
    })
    .catch(() => {
      reject(false)
    })
    .then(res => {
      if (res && res.data) {
        resolve(res.data);
      }
    })
  });
}

export const getAvailabilityPackages = ({ form, packageId }) => {
  const { arrivalDate, departureDate, guests, childages} = processForm(form);
  let url = `${CONNECT}/packages/package?id=${CONNECT_CLIENT}&arrivalDate=${arrivalDate}&departureDate=${departureDate}&guests=${guests}&childrenAges=${childages}&packageId=${packageId}`

  return new Promise((resolve, reject) => {
    request({
      method: "GET",
      url
    })
    .catch(() => {
      reject(false)
    })
    .then(res => {
      if (!res || !res.data) reject(false);
      resolve(res.data)
    })
  });
}

export const parseFormFromQueryString = (query) => {
  const _guests = [];
  // if we are on a multiroom form
  if (typeof query.adult === "string" && query.adult.indexOf("-") >= 0) {
    const adults = query.adult.split("-");
    adults.forEach((adult, index) => {
      if (!_guests[index]) _guests[index] = [];
      for (let i = 0; i < parseInt(adult); i++) {
        _guests[index].push({ type: "adult" })
      }
    })
    if (query.childages) {
      const children = query.childages.toString().split("-");
      children.forEach((child, index) => {
        if (!child.length) return;
        if (!_guests[index]) _guests[index] = [];
        if (typeof child === "string") {
          child.split(",").forEach(childAge => {
            _guests[index].push({
              type: "child",
              age: childAge
            })
          })
        } else {
          _guests[index].push({
            type: "child",
            age: child
          })
        }
      })

    }

  } else {
    // package / no multiroom case
    for (let i = 0; i < parseInt(query.adult); i++) {
      _guests.push({ type: "adult" })
    }

    if (query.childages) {
      if (typeof query.childages === "string") {
        query.childages.split(",").forEach(childAge => {
          _guests.push({
            type: "child",
            age: childAge
          })
        })
      } else {
        _guests.push({
          type: "child",
          age: query.childages
        })
      }
    }
  }

  return {
    arrivalDate: query.startdate ? fromYYYYMMDD(query.startdate) : null,
    departureDate: query.enddate ? fromYYYYMMDD(query.enddate) : null,
    guests: _guests
  }
}

export const processForm = (form) => {
  const arrivalDate = toYYYYMMDD(form.arrivalDate);
  const departureDate = toYYYYMMDD(form.departureDate);
  let guests = [];
  let children = [];
  let childages = [];
  if (form.guests[0] && Array.isArray(form.guests[0])) {
    form.guests.forEach(groupOfGuests => {
      guests.push(groupOfGuests.length);
      children = groupOfGuests.filter(guest => guest.type === "child" || guest.type === "junior");
      childages.push(children.map(child => child.age).join(",") || "")
    })
  } else {
    guests = form.guests.length
    children = form.guests.filter(guest => guest.type === "child" || guest.type === "junior");
    childages = children.map(child => child.age).join(",") || "";
  }

  return {
    arrivalDate,
    departureDate,
    guests,
    childages,
    passholders: form.passholders
  }
}

export const addLodgingToCart = ({ product, price, form, type, complementary_products = [], quantity }) => {
  const url = `${CONNECT}/checkout/add-to-cart-lodging`;
  let arrival_date, departure_date, guests, childrenAges;
  if (type !== "activity" && type !== "restaurant") {
    ({ arrivalDate: arrival_date, departureDate: departure_date, guests, childages: childrenAges } = processForm(form));
  } else {
    childrenAges = "";
    guests = form.guests;
    arrival_date = form.range.from_date;
    departure_date = form.range.to_date;
  }

  const { uuid, name } = product;

  const complementaryProducts = [];
  if (complementary_products) {
    complementary_products.forEach(extra => {
      let complementaryProduct = { id: extra.product_id }
      if (extra.requirement) {
        complementaryProduct.requirement = {
          value: extra.requirement.value
        }
      }
      if (extra.quantity) {
        complementaryProduct.quantity = parseInt(extra.quantity);
      }
      complementaryProducts.push(complementaryProduct)
    })
  }

  let session = getSession();
  // Build the item object
  const item = {
    parent: product.supplier_uuid,
    uuid,
    name: name,
    arrival_date,
    departure_date,
    guests,
    childrenAges,
    price,
    complementary_products: complementaryProducts
  };

  // If quantity is present, duplicate the item that many times
  let items = [];
  if (quantity && quantity > 1) {
    for (let i = 0; i < quantity; i++) {
      items.push({ ...item });
    }
  } else {
    items = [item];
  }

  const data = {
    client: CONNECT_CLIENT,
    session: session?.uuid,
    items
  };

  async function fetchData() {
    if (!data.session) {
      const res = await generateSession();
      if (res && res.uuid) {
        session = res;
        data.session = session.uuid;
      }
    }
  
    return new Promise((resolve, reject) => {
      request({
        method: "POST",
        url,
        data
      })
        .catch(() => {
          reject(false);
        })
        .then(async res => {
          gtmFunction("add_to_cart", data.items.concat(complementary_products || []));
          if (!res || !res.data || res.status !== 200 || res.data.status === "ko") {
            reject(false);
            return;
          }
          setSession({
            ...session,
            updated_at: res.data.updated_at,
            products: res.data.products,
          });
          const notes = complementary_products.map(product => {
            if (product.activate_comments && product.note && product.note.length > 0) return product.note;
            return undefined; 
          }).filter(note => note !== undefined);
          if (notes && notes.length > 0) {
            await addNotesToSession(notes);
          }
          resolve(res.data);
        });
    });
  }
  
  return fetchData();
}

export const addNotesToSession = (notes) => {
  let session = getSession();
  return new Promise((resolve, reject) => {
    request({
      method: "POST",
      url: `${CONNECT}/checkout/session/notes`,
      data: {
        client: CONNECT_CLIENT,
        session: session?.uuid,
        notes: notes.join(" || ")
      }
    })
    .then(r => {
      resolve(r)
    })
    .catch((e) => reject(e))
  })
}

export const addPackageToCart = ({ products, form }) => {
  const url = `${CONNECT}/checkout/add-to-cart-lodging`;
  let arrival_date, departure_date, guests, childrenAges, passholders;
  ({ arrivalDate: arrival_date, departureDate: departure_date, guests, childages: childrenAges, passholders } = processForm(form));
  const items = [];
  let complementary_products = [];
  products.forEach(item => {
    complementary_products = [];
    if (item.type === "lift-tickets") {
      items.push({
        supplierId: item.supplierId,
        productId: item.uuid,
        variantId: item.variantId,
        name: item.name,
        date: item.date,
        package: item.package
      })
      return;
    }
    if (item.type === "rental" || item.type === "lesson") {
      const indexOfThisRentalOrLessonInPassholders = products.filter(product => product.type === "rental" || product.type === "lesson").indexOf(item);
      items.push({
        supplierId: item.supplierId,
        productId: item.uuid,
        variantId: item.variantId,
        name: item.name,
        date: item.date,
        package: item.package,
        days: differenceInDays(new Date(arrival_date), new Date(departure_date)),
        requirements: passholders[indexOfThisRentalOrLessonInPassholders].requirements.map(req => {
          return { identifier: req.identifier, value: req.value }
        })
      })
      return;
    }

    // lodging item
    if (item.type !== "activity" && item.type !== "restaurant") {
      if (item.complementary_products) {
        item.complementary_products.forEach(extra => {
          let complementaryProduct = { id: extra.product_id }
          if (extra.requirement) {
            complementaryProduct.requirement = {
              value: extra.requirement.value
            }
          }
          complementary_products.push(complementaryProduct)
        })
      }
      items.push({
        uuid: item.uuid,
        name: item.name,
        arrival_date,
        departure_date,
        guests,
        childrenAges,
        price: item.selectedPrice,
        complementary_products,
        package: item.package
      })

    } else {
      if (item.complementary_products) {
        item.complementary_products.forEach(extra => {
          let complementaryProduct = { id: extra.product_id, quantity: parseInt(extra.quantity) }
          if (extra.requirement) {
            complementaryProduct.requirement = {
              value: extra.requirement.value
            }
          }
          complementary_products.push(complementaryProduct)
        })
      }
      items.push({
        uuid: item.uuid,
        name: item.name,
        arrival_date: item.arrival_date,
        departure_date: item.departure_date,
        guests: guests.length,
        childrenAges: "",
        price: item.selectedPrice,
        complementary_products,
        package: item.package
      })
    }
  })

  let session = getSession();
  const data = {
    client: CONNECT_CLIENT,
    session: session?.uuid,
    items
  }

  async function fetchData() {
    if (!data.session) {
      const res = await generateSession();
      if (res && res.uuid) {
        session = res;
        data.session = session.uuid;
      }
    }
  
    return new Promise((resolve, reject) => {
      request({
        method: "POST",
        url,
        data
      })
        .catch(() => {
          reject(false);
        })
        .then(async res => {
          gtmFunction("add_to_cart", data.items);
          if (!res || !res.data || res.status !== 200) reject(false);
          setSession({
            ...session,
            updated_at: res.data.updated_at,
            products: res.data.products,
          });
          const notes = complementary_products.map(product => {
            if (product.activate_comments && product.note && product.note.length > 0) return product.note;
            return undefined; 
          }).filter(note => note !== undefined);
          if (notes && notes.length > 0) {
            await addNotesToSession(notes);
          }
          resolve(res.data);
        });
    });
  }
  
  return fetchData();
}

export const getCalendarAvailability = ({ id, date }) => {
  const endDate = getLastDayOfNextMonth(date);
  let url = `${CONNECT}/lodging/availability?id=${CONNECT_CLIENT}&product=${id}&arrivalDate=${toYYYYMMDD(date)}&departureDate=${toYYYYMMDD(endDate)}`;
  return new Promise((resolve, reject) => {
    request({
      method: "GET",
      url
    })
    .catch(() => {
      reject(false)
    })
    .then((res) => {
      resolve(res.data);
    })
  })
}

function getLastDayOfNextMonth(date) {
  const nextMonth = new Date(date.getFullYear(), date.getMonth() + 1);
  nextMonth.setMonth(nextMonth.getMonth() + 1);
  nextMonth.setDate(0);
  return nextMonth;
}

export function parsedType (type) {
  if (type === "apartment" || type === "hotel" || type === "room" || type === "cabin") return "lodging";
  if (type === "restaurants") return "restaurant";
  if (type === "activities") return "activity";
  if (type === "lift-tickets" || type === "lt-date-first") return "lift-ticket";
  return type;
}

export const getAvailabilityLifts = ({ date, productId, packageId }) => {
  let url = `${CONNECT}/lift-tickets?id=${CONNECT_CLIENT}&date=${toYYYYMMDD(date)}&product=${productId}`
  return new Promise((resolve, reject) => {
    request({
      method: "GET",
      url
    })
    .catch(() => {
      reject(false)
    })
    .then(res => {
      if (res && res.data) {
        resolve(res.data);
      }
    })
  });
}

export const getSearchFormConfiguration = (item) => {
  let searchFormConfiguration = {}
  if (item && item.search_form_configuration) {
    searchFormConfiguration = { ...item.search_form_configuration };
  }

  let customBlackoutPeriods = [];
  if(item.blackout_periods && item.blackout_periods.length > 0) {
    customBlackoutPeriods = item.blackout_periods;
  }

  if(searchFormConfiguration.blackout_periods && searchFormConfiguration.blackout_periods.length > 0) {
    searchFormConfiguration.blackout_periods.forEach(period => {
      if(period.From <= period.To) {
        customBlackoutPeriods.push({...period});
      }
    })
  }

  searchFormConfiguration.blackout_periods = customBlackoutPeriods;

  return searchFormConfiguration;
}

export const getConfigurationForDate = (item, date) => {
  // If supplier has multiple configurations, use them
  if (item && item.search_form_configurations && item.search_form_configurations.length > 0) {
    const checkDate = new Date(date);
    
    // First, try to find a configuration with fitting date range
    const applicableConfig = item.search_form_configurations.find(config => {
      if (!config) return false;
      
      const { config_valid_from, config_valid_to } = config;
      
      // Skip configurations without date ranges (these are fallbacks)
      if (!config_valid_from && !config_valid_to) return false;
      
      // Check if this date falls within the configuration's validity period
      const configFrom = config_valid_from ? new Date(config_valid_from) : null;
      const configTo = config_valid_to ? new Date(config_valid_to) : null;
      
      if (configFrom && checkDate < configFrom) return false;
      if (configTo && checkDate > configTo) return false;
      
      return true;
    });
    
    if (applicableConfig) {
      return applicableConfig;
    }
    
    // If no fitting date configuration found, look for a fallback configuration (no date range)
    const fallbackConfig = item.search_form_configurations.find(config => {
      if (!config) return false;
      
      const { config_valid_from, config_valid_to } = config;
      
      // This is a fallback configuration (no date range specified)
      return !config_valid_from && !config_valid_to;
    });
    
    if (fallbackConfig) {
      return fallbackConfig;
    }
  }
  
  // Fallback to the old single configuration approach
  if (item && item.search_form_configuration) {
    return item.search_form_configuration;
  }
  
  return {};
}

export default getAvailability;